import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { verdictChip, verdictColor } from '../../utils/trainingVerdict';

const STATUS_LABELS = {
  in_progress: { label: 'In Progress', color: 'info' },
  awaiting_formulation: { label: 'Awaiting Formulation', color: 'warning' },
  awaiting_evaluation: { label: 'Evaluating', color: 'warning' },
  completed: { label: 'Completed', color: 'success' },
  timed_out: { label: 'Timed Out', color: 'error' },
  abandoned: { label: 'Cancelled', color: 'default' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  return `${mins} min`;
};

const SessionCard = ({ session, onViewFeedback, onContinue }) => {
  const {
    _id,
    scenarioId,
    status,
    startedAt,
    completedAt,
    durationSeconds,
    mode,
    attemptNumber,
  } = session;
  // Handle both field naming conventions from different API responses
  const scenarioName = session.scenarioName || session._scenarioName || 'Training Session';
  const compositeScore = session.compositeScore ?? session.aiEvaluation?.compositePercentage ?? null;
  const passFail = session.passFail || session.aiEvaluation?.passFail || null;

  const statusConfig = STATUS_LABELS[status] || { label: status, color: 'default' };

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {scenarioName || 'Training Session'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(startedAt)}
              {durationSeconds ? ` | ${formatDuration(durationSeconds)}` : ''}
              {attemptNumber ? ` | Attempt ${attemptNumber}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {mode && (
              <Chip
                label={mode === 'assessment' ? 'GRADED' : 'Practice'}
                size="small"
                variant="outlined"
                color={mode === 'assessment' ? 'error' : 'default'}
              />
            )}
            <Chip label={statusConfig.label} size="small" color={statusConfig.color} />
          </Box>
        </Box>

        {compositeScore != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="h6" fontWeight={700} color={`${verdictColor(passFail)}.main`}>
              {Math.round(compositeScore)}%
            </Typography>
            {passFail && (
              <Chip
                label={verdictChip(passFail).label}
                size="small"
                color={verdictChip(passFail).color}
              />
            )}
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5 }}>
        {status === 'in_progress' && onContinue && (
          <Button size="small" variant="contained" onClick={() => onContinue(_id)}>
            Continue
          </Button>
        )}
        {(status === 'completed' || compositeScore != null) && onViewFeedback && (
          <Button size="small" variant="outlined" onClick={() => onViewFeedback(_id)}>
            View Feedback
          </Button>
        )}
        {status === 'abandoned' && onViewFeedback && (
          <Button size="small" variant="outlined" onClick={() => onViewFeedback(_id)}>
            View Transcript
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default SessionCard;
